import { FC, useState } from 'react';
import { Alert, AlertTitle, Box } from '@mui/material';
import { Text, Image } from '@concepta/react-material-ui';

import { isMobile } from 'utils/isMobile';
import FormLabel from '../FormLabel';

interface Props {
  deliveredImages: string[];
  deliveredDate?: Date;
}

const DeliveryConfirmation: FC<Props> = ({
  deliveredImages,
  deliveredDate,
}) => {
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  const handleImageError = (imageUrl: string) => {
    setImageErrors(prev => ({ ...prev, [imageUrl]: true }));
  };
  const formatDeliveryDateTime = () => {
    if (!deliveredDate) {
      return { date: 'Date not specified', time: 'Time not specified' };
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const deliveryDay = new Date(
      deliveredDate.getFullYear(),
      deliveredDate.getMonth(),
      deliveredDate.getDate(),
    );

    const isToday = deliveryDay.getTime() === today.getTime();

    const dateStr = deliveredDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    const timeStr = deliveredDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    const dayPrefix = isToday
      ? 'Today'
      : deliveredDate.toLocaleDateString('en-US', { weekday: 'long' });
    const formattedTime = `${dayPrefix}, ${timeStr}`;

    return { date: dateStr, time: formattedTime };
  };

  const { date, time } = formatDeliveryDateTime();

  return (
    <>
      <Box my={2} px={2}>
        <Alert severity="success">
          <AlertTitle>Delivery Confirmed</AlertTitle>
          Your stroller has been successfully delivered and is ready for your
          adventure!
        </Alert>
      </Box>

      {deliveredImages?.length > 0 ? (
        <Box px={2}>
          <Box display="flex" alignItems="center">
            <FormLabel mb={0}>Your Delivered Stroller</FormLabel>
          </Box>

          <Box
            display="flex"
            flexWrap="wrap"
            gap={2}
            mt={1}
            justifyContent={isMobile ? 'center' : 'flex-start'}
          >
            {deliveredImages.map((imageUrl, index) => (
              <Box key={`delivered-image-${index}`}>
                {!imageErrors[imageUrl] ? (
                  <Image
                    src={imageUrl}
                    alt="Delivered stroller"
                    onError={() => handleImageError(imageUrl)}
                    sx={{
                      width: 'auto',
                      height: isMobile ? 200 : 250,
                      maxWidth: isMobile ? '100%' : 'none',
                      objectFit: 'contain',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.300',
                      display: 'block',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: isMobile ? '100%' : 200,
                      height: isMobile ? 200 : 250,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'grey.100',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.300',
                    }}
                  >
                    <Text color="grey.600" fontSize={14}>
                      Image unavailable
                    </Text>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}

      <Box mt={4} px={2}>
        <Box display="flex" alignItems="center">
          <FormLabel mb={0}>Delivery Information</FormLabel>
        </Box>

        <Box mt={2}>
          <Alert severity="info">
            <AlertTitle>Delivery Time</AlertTitle>
            <Text fontWeight="600">{time}</Text>
            <Text>{date}</Text>
          </Alert>
        </Box>
      </Box>
    </>
  );
};

export default DeliveryConfirmation;
